import { useEffect, useState } from "react";
import { UserX, Smartphone, Trash2 } from "lucide-react";

function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let r;
    const f = () => { setT((x) => x + 1); r = requestAnimationFrame(f); };
    r = requestAnimationFrame(f);
    return () => cancelAnimationFrame(r);
  }, []);
  return t;
}

function GlyphAnonymous() {
  const t = useTick();
  const ph = (t / 90) % 1;
  return (
    <svg viewBox="0 0 280 100">
      <g fontFamily="var(--mono)" fontSize="11" fill="var(--fg-dim)">
        <text x="0" y="20">name:</text>
        <rect x="50" y="9" width="120" height="14" fill="var(--fg)" />
        <text x="0" y="44">email:</text>
        <rect x="50" y="33" width="160" height="14" fill="var(--fg)" />
        <text x="0" y="68">payment:</text>
        <rect x="64" y="57" width="140" height="14" fill="var(--fg)" />
        <text x="0" y="92" fill="var(--accent)" letterSpacing="2">acct_id: —</text>
      </g>
      <rect x={ph * 280 - 30} y="0" width="30" height="100" fill="var(--accent)" opacity="0.12" />
    </svg>
  );
}

function GlyphOnDevice() {
  const t = useTick();
  const ph = (t / 80) % 1;
  return (
    <svg viewBox="0 0 280 100">
      <rect x="20" y="14" width="80" height="72" rx="6" fill="none" stroke="var(--fg)" strokeWidth="1" />
      <rect x="28" y="22" width="64" height="50" fill="var(--bg-1)" stroke="var(--rule)" strokeWidth="0.5" />
      <circle cx="60" cy="80" r="2" fill="var(--fg-faint)" />
      <g transform="translate(60, 47)">
        <circle r="8" fill="none" stroke="var(--accent)" strokeWidth="1" opacity={0.4 + Math.sin(t / 12) * 0.4} />
        <circle r="3" fill="var(--accent)" />
      </g>
      <text x="120" y="40" fontFamily="var(--mono)" fontSize="11" fill="var(--fg-dim)" letterSpacing="1">HASH</text>
      <text x="120" y="56" fontFamily="var(--mono)" fontSize="11" fill="var(--accent)" letterSpacing="1">never reverses</text>
      <text x="120" y="74" fontFamily="var(--mono)" fontSize="9" fill="var(--fg-faint)" letterSpacing="2">SHA-256 · ANONYMOUS</text>
      <line x1="100" y1="50" x2="120" y2="50" stroke="var(--warn)" strokeWidth="1" strokeDasharray="2 2" opacity={ph < 0.5 ? 1 : 0} />
    </svg>
  );
}

function GlyphNothingRetained() {
  const t = useTick();
  const ph = (t / 70) % 1;
  return (
    <svg viewBox="0 0 280 100">
      <line x1="0" y1="50" x2="200" y2="50" stroke="var(--rule)" strokeWidth="0.5" strokeDasharray="3 3" />
      {[0, 0.25, 0.5, 0.75].map((o, i) => {
        const p = (ph + o) % 1;
        const x = p * 200;
        const fade = p < 0.7 ? 1 : Math.max(0, 1 - (p - 0.7) / 0.3);
        return (
          <g key={i} opacity={fade}>
            <rect x={x - 6} y="44" width="12" height="12" fill="var(--accent)" />
          </g>
        );
      })}
      <g transform="translate(220, 50)">
        <circle r="20" fill="none" stroke="var(--warn)" strokeWidth="1" />
        <line x1="-14" y1="14" x2="14" y2="-14" stroke="var(--warn)" strokeWidth="1" />
      </g>
      <text x="140" y="92" fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">
        NOTHING IS WRITTEN
      </text>
    </svg>
  );
}

const ITEMS = [
  {
    num: "01 / Identity",
    Icon: UserX,
    title: "Hash-based authentication. We never see your email.",
    body: "Your email becomes a SHA-256 hash before it touches our servers. Cryptographically impossible to reverse — even our database can't identify you.",
    Glyph: GlyphAnonymous,
  },
  {
    num: "02 / Custody",
    Icon: Smartphone,
    title: "No conversation history. No prompt logging. No training.",
    body: "Your prompts aren't saved or analyzed. We don't train models on your data. There's no chat history stored on our servers.",
    Glyph: GlyphOnDevice,
  },
  {
    num: "03 / Retention",
    Icon: Trash2,
    title: "Privacy by architecture, not by policy.",
    body: "We didn't add privacy as a feature. We built it into the system. Inference runs on stateless nodes — the trail you didn't leave is the trail no one can subpoena.",
    Glyph: GlyphNothingRetained,
  },
];

export default function Guarantees() {
  return (
    <section id="guarantees">
      <div className="container">
        <div className="section-head">
          <div className="label reveal">/ 02 — Three Guarantees</div>
          <div>
            <h2 className="reveal d1">Three architectural guarantees. Not three promises.</h2>
            <p className="lede reveal d2">
              Every claim below is enforced by the way the system is built — not by a privacy policy you have to trust.
            </p>
          </div>
        </div>

        <div className="guarantees-grid reveal d2">
          {ITEMS.map(({ num, Icon, title, body, Glyph }) => (
            <div key={num} className="guarantee">
              <div className="num">
                <Icon size={14} strokeWidth={1.5} style={{ verticalAlign: "-2px", marginRight: 8 }} />
                {num}
              </div>
              <h3>{title}</h3>
              <p>{body}</p>
              <div className="glyph"><Glyph /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
